import { ChevronRight, PlayCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { groupProductsByCategory, type Product } from '../Product/productData';
import { cn } from '@/lib/utils';

function ProductRow({ product }: { product: Product }) {
  return (
    <li title={product.secondary} className="group relative flex min-h-14 items-center gap-3 border-t border-white/[0.07] px-3 py-2 transition-colors lg:min-h-12 lg:py-1.5 first:border-t-0 hover:bg-white/[0.05] focus-within:bg-white/[0.05] active:bg-white/[0.08]">
      <Link
        to={product.to}
        aria-label={product.primary}
        className="absolute inset-0 z-10 outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary"
      />
      {product.icon ? (
        <span aria-hidden className="w-7 shrink-0 select-none text-center text-xl leading-none">
          {product.icon}
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[0.95rem] font-medium leading-snug text-foreground">{product.primary}</span>
        <span className="block truncate text-[0.8125rem] leading-snug text-muted-foreground">{product.summary}</span>
      </span>
      {product.twitterLink ? (
        <a
          href={product.twitterLink}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Watch a demo of ${product.primary}`}
          title="Watch a demo"
          className="relative z-20 -my-1 inline-flex size-11 shrink-0 items-center justify-center rounded-md text-white/60 transition-colors hover:bg-white/10 hover:text-white lg:size-8"
        >
          <PlayCircle aria-hidden className="size-4" />
        </a>
      ) : null}
      <ChevronRight aria-hidden className="size-4 shrink-0 text-white/40" />
    </li>
  );
}

function ProductSection({ title, products }: { title: string; products: Product[] }) {
  return (
    <section aria-label={title}>
      <h2 className="label-mono mb-2">{title}</h2>
      <ul className="overflow-hidden rounded-2xl bg-[#111] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.07)]">
        {products.map((product) => (
          <ProductRow key={product.to} product={product} />
        ))}
      </ul>
    </section>
  );
}

type ProductCatalogProps = {
  products: Product[];
  /** The featured rows, shown only in the default view with no search or filter. */
  featured?: Product[];
};

export default function ProductCatalog({ products, featured = [] }: ProductCatalogProps) {
  const featuredPaths = new Set(featured.map((product) => product.to));
  const groups = groupProductsByCategory(products.filter((product) => !featuredPaths.has(product.to)));
  const [firstGroup, ...restGroups] = groups;
  const leadGroupInFirstColumn = featured.length > 0 && firstGroup;

  return (
    <div className={cn('grid grid-cols-1 items-start gap-6 lg:gap-4', groups.length + (featured.length ? 1 : 0) > 1 && 'lg:grid-cols-3')}>
      {featured.length > 0 ? (
        <div className="flex flex-col gap-6 lg:gap-4">
          <ProductSection title="Featured" products={featured} />
          {leadGroupInFirstColumn ? <ProductSection title={firstGroup.label} products={firstGroup.products} /> : null}
        </div>
      ) : null}
      {(leadGroupInFirstColumn ? restGroups : groups).map((group) => (
        <ProductSection key={group.category} title={group.label} products={group.products} />
      ))}
    </div>
  );
}
